import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  HiOutlineArrowLeft,
  HiOutlineShoppingBag,
  HiOutlineCheckBadge,
} from "react-icons/hi2";
import { FiLoader, FiMinus, FiPlus, FiCheck } from "react-icons/fi";
import toast from "react-hot-toast";
import SummaryApi from "../common/index";
import { useCart } from "../context/CartContext";

const ProductDetails = () => {
  const { id: encodedId } = useParams();
  const navigate = useNavigate();
  const { fetchCartCount } = useCart();

  const [product, setProduct] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState("");
  const [localQty, setLocalQty] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [cartData, setCartData] = useState([]);

  const productId = useMemo(() => {
    try {
      return String(window.atob(encodedId));
    } catch (e) {
      return null;
    }
  }, [encodedId]);
// This ensures that whenever the productId changes, the page jumps to the top
useEffect(() => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant", // Use "instant" to prevent the user from seeing the jump
  });
}, [productId]); // Add productId here
  const currentCartItem = useMemo(() => {
    if (!selectedUnit || !cartData.length) return null;
    return cartData.find(
      (item) =>
        String(item.product_id) === String(productId) &&
        String(item.product_unit_id) === String(selectedUnit.product_unit_id),
    );
  }, [cartData, productId, selectedUnit]);

  useEffect(() => {
    setLocalQty(currentCartItem ? currentCartItem.quantity : 0);
  }, [currentCartItem, selectedUnit]);

  const IMAGE_BASE_URL =
    SummaryApi?.productImage?.url?.replace("/api/uploads", "") ||
    "http://192.168.1.254:3000";

  const getUserId = useCallback(() => {
    const data = localStorage.getItem("user");
    if (!data) return null;
    try {
      const user = JSON.parse(data);
      return user?.user_id || user?.id || null;
    } catch {
      return null;
    }
  }, []);

  const showSuccessToast = (message = "Cart updated successfully!") => {
    toast.custom(
      (t) => (
        <div
          className={`${t.visible ? "animate-enter" : "animate-leave"} max-w-[calc(100vw-2rem)] sm:max-w-md w-full bg-white shadow-2xl rounded-[2rem] flex flex-col border-b-[6px] border-rose-500 overflow-hidden`}
        >
          <div className="p-4 sm:p-5 flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="h-12 w-12 sm:h-14 sm:w-14 shrink-0 rounded-2xl bg-rose-50 flex items-center justify-center border border-rose-100">
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full border-2 border-rose-500 flex items-center justify-center">
                  <FiCheck className="text-rose-500 text-lg sm:text-xl stroke-[3]" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base sm:text-lg font-black text-rose-950 leading-tight truncate">
                  {message}
                </p>
                <p className="text-xs sm:text-sm font-medium text-rose-400">
                  Inventory Synced
                </p>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="text-rose-600 font-black text-xs sm:text-sm uppercase pl-2 pr-1"
            >
              Close
            </button>
          </div>
        </div>
      ),
      { duration: 500 },
    );
  };

  const fetchCartData = useCallback(async () => {
    const userId = getUserId();
    try {
      if (userId) {
        const res = await fetch(`${SummaryApi.getCartById.url}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        });
        const result = await res.json();
        if (result.status) setCartData(result.data);
      } else {
        setCartData(JSON.parse(sessionStorage.getItem("guest_cart") || "[]"));
      }
    } catch (err) {
      console.error(err);
    }
  }, [getUserId]);

  // Unified sync logic for both Add Button and Increment/Decrement
  const syncCart = async (newQty) => {
    if (!selectedUnit || isSyncing) return;
    setIsSyncing(true);
    const userId = getUserId();

    if (!userId) {
      let cart = JSON.parse(sessionStorage.getItem("guest_cart") || "[]");
      const idx = cart.findIndex(
        (i) =>
          String(i.product_id) === String(productId) &&
          String(i.product_unit_id) === String(selectedUnit.product_unit_id),
      );
      if (newQty === 0) {
        if (idx > -1) cart.splice(idx, 1);
      } else {
        if (idx > -1) cart[idx].quantity = newQty;
        else
          cart.push({
            product_id: productId,
            quantity: newQty,
            product_unit_id: selectedUnit.product_unit_id,
          });
      }
      sessionStorage.setItem("guest_cart", JSON.stringify(cart));
      await fetchCartCount();
      await fetchCartData();
      setIsSyncing(false);
      return;
    }

    try {
      let res;
      if (newQty === 0 && currentCartItem?.cart_id) {
        res = await fetch(SummaryApi.cartDelete.url, {
          method: SummaryApi.cartDelete.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id: currentCartItem.cart_id }),
        });
      } else if (!currentCartItem) {
        res = await fetch(SummaryApi.addToCart.url, {
          method: SummaryApi.addToCart.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            product_id: productId,
            product_unit_id: selectedUnit.product_unit_id,
            quantity: newQty,
            product_name: product.product_title,
            product_type: "normal product",
          }),
        });
      } else {
        res = await fetch(SummaryApi.cartUpdate.url, {
          method: SummaryApi.cartUpdate.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            product_id: productId,
            product_unit_id: selectedUnit.product_unit_id,
            quantity: newQty,
            cart_id: currentCartItem.cart_id,
            product_name: product.product_title,
            product_type: "normal product",
          }),
        });
      }
      if (res?.ok) {
        await fetchCartCount();
        await fetchCartData();
        if (newQty > 0)
          showSuccessToast(
            currentCartItem ? "Quantity updated" : "Added to cart!",
          );
      }
    } catch (err) {
      toast.error("Sync failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleIncrement = () => {
    const nextQty = localQty + 1;
    setLocalQty(nextQty);
    syncCart(nextQty);
  };

  const handleDecrement = () => {
    if (localQty <= 0) return;
    const nextQty = localQty - 1;
    setLocalQty(nextQty);
    syncCart(nextQty);
  };

  const fetchProductDetails = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await fetch(SummaryApi.getProductById.url, {
        method: SummaryApi.getProductById.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: productId }),
      });
      const result = await res.json();
      if (result.status) {
        setProduct(result.data);
        setActiveImage(result.data.product_image1);
        if (result.data.units?.length > 0)
          setSelectedUnit(result.data.units[0]);
      }
    } catch (err) {
      toast.error("Error loading product");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProductDetails();
    fetchCartData();
  }, [fetchProductDetails, fetchCartData]);

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center">
        <FiLoader className="animate-spin text-rose-500" size={40} />
      </div>
    );
  }
  const isSubscriber =
    JSON.parse(localStorage.getItem("user") || "{}")?.is_subscriber === 1;

  const isLeaf =
    product?.product_title?.toLowerCase().includes("leaf") ||
    product?.product_title?.toLowerCase().includes("sakhi");
  const gallery = [
    product.product_image1,
    product.product_image2,
    product.product_image3,
    product.product_image4,
    product.product_image5,
  ].filter((img) => img);
  const isOutOfStock = product.is_stock_available === 0;

  return (
    <div className="min-h-screen bg-[#FFF0F3] py-20 px-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={() => navigate("/products")}
          className="flex items-center gap-2 text-rose-950 font-black uppercase text-xs tracking-widest mb-10"
        >
          <HiOutlineArrowLeft size={20} /> Back to Collection
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="space-y-6">
            <div
              className={`aspect-square bg-white rounded-[3rem] overflow-hidden shadow-2xl flex items-center justify-center relative ${isOutOfStock ? "grayscale" : ""}`}
            >
              {isOutOfStock && (
                <div className="absolute inset-0 bg-white/20 backdrop-blur-[2px] flex items-center justify-center z-10">
                  <span className="bg-red-600 text-white px-8 py-3 rounded-full font-black uppercase tracking-widest">
                    Unavailable
                  </span>
                </div>
              )}
              <img
                src={`${IMAGE_BASE_URL}/${activeImage}`}
                className="w-full h-full object-contain p-10"
                alt={product.product_title}
              />
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {gallery.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(img)}
                  className={`w-24 h-24 flex-shrink-0 rounded-2xl overflow-hidden border-4 bg-white transition-all ${activeImage === img ? "border-rose-500" : "border-transparent"}`}
                >
                  <img
                    src={`${IMAGE_BASE_URL}/${img}`}
                    className="w-full h-full object-contain p-2"
                    alt=""
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col">
            <h1 className="text-4xl font-black text-rose-950 uppercase mb-2">
              {product.product_title}
            </h1>
            <p className="text-xl font-medium text-rose-900/40 mb-8 italic">
              {product.subtitle} | {product.product_size}
            </p>

            <div className="mb-8">
              <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-4">
                Select Pack Size
              </p>
              <div className="flex flex-wrap gap-3">
                {product.units?.map((unit) => (
                  <button
                    key={unit.product_unit_id}
                    disabled={isOutOfStock}
                    onClick={() => setSelectedUnit(unit)}
                    className={`px-6 py-3 rounded-2xl font-black text-sm transition-all border-2 ${selectedUnit?.product_unit_id === unit.product_unit_id ? "bg-rose-500 border-rose-500 text-white shadow-lg" : "bg-white border-rose-100 text-rose-400 hover:border-rose-300"} ${isOutOfStock ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Pack {unit.pack_size}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Section */}
            <div className="mb-10">
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-rose-300 uppercase tracking-widest">
                  Price per pad
                </p>

                <div className="flex items-center gap-3 flex-wrap">
                  {isSubscriber && isLeaf ? (
                    <>
                      {/* Discounted Unit Price */}
                      <span className="text-xl font-bold line-through text-rose-300">
                        ₹15.00
                      </span>
                      <span className="text-3xl font-black text-rose-950">
                        ₹12.00
                      </span>
                      <span className="bg-emerald-100 text-emerald-600 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter border border-emerald-200">
                        Subscriber Benefit
                      </span>
                    </>
                  ) : (
                    <>
                      {/* Regular Unit Price */}
                      <span className="text-xl font-bold text-rose-300 line-through">
                        ₹{product.actual_price || "15.00"}
                      </span>
                      <span className="text-3xl font-black text-rose-950">
                        ₹{product.selling_price}
                      </span>
                    </>
                  )}
                </div>

                {/* Total Pack Price Calculation */}
                <div className="mt-4">
                  <p className="text-[10px] font-bold text-rose-300 uppercase tracking-widest">
                    Total Pack Price
                  </p>
                  <span className="text-2xl font-black text-rose-950">
                    ₹
                    {(
                      (isSubscriber && isLeaf ? 12 : product.selling_price) *
                      (selectedUnit?.pack_size || 1)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Quantity Controls - Now trigger API on click */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <div className="flex-[0.4] bg-white h-20 rounded-3xl flex items-center justify-between px-6 shadow-lg border border-rose-100">
                <button
                  onClick={handleDecrement}
                  disabled={isSyncing || localQty === 0}
                  className="text-rose-500 p-2 disabled:opacity-20"
                >
                  <FiMinus size={24} />
                </button>
                <span className="text-2xl font-black text-rose-950">
                  {isSyncing ? (
                    <FiLoader
                      className="animate-spin text-rose-300"
                      size={20}
                    />
                  ) : (
                    localQty
                  )}
                </span>
                <button
                  onClick={handleIncrement}
                  disabled={isSyncing}
                  className="text-rose-500 p-2"
                >
                  <FiPlus size={24} />
                </button>
              </div>

              {/* Main Button Logic: if in cart, show "Added to Cart" and navigate to cart on click */}
              <button
                onClick={() =>
                  currentCartItem ? navigate("/cart") : syncCart(1)
                }
                disabled={isSyncing || isOutOfStock}
                className={`flex-1 h-20 rounded-3xl font-black uppercase tracking-widest flex items-center justify-center gap-3 shadow-xl transition-all px-3 py-3 ${
                  currentCartItem
                    ? "bg-white text-rose-500 border-2 border-rose-500 hover:bg-rose-50"
                    : "bg-rose-500 text-white hover:bg-rose-600"
                } disabled:opacity-50`}
              >
                {isSyncing ? (
                  <FiLoader className="animate-spin" size={24} />
                ) : currentCartItem ? (
                  <FiCheck size={24} />
                ) : (
                  <HiOutlineShoppingBag size={24} />
                )}
                {currentCartItem ? "Added to Cart" : "Add to Cart"}
              </button>
            </div>

            <div className="p-6 sm:p-8 bg-white/40 backdrop-blur-sm rounded-[2.5rem] border border-white w-full">
              <h3 className="flex items-center gap-2 text-sm font-black text-rose-950 uppercase tracking-widest mb-4">
                <HiOutlineCheckBadge className="text-rose-500" size={20} />{" "}
                About Product
              </h3>
              <style>{`
                .rich-text-content { width: 100%; font-size: 0.95rem; }
                .rich-text-content * { max-width: 100% !important; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important; box-sizing: border-box; }
                .rich-text-content ul, .rich-text-content ol { padding-left: 1.25rem; margin: 1rem 0; list-style-type: disc; }
                .rich-text-content p { margin-bottom: 0.75rem; line-height: 1.6; }
              `}</style>
              <div
                className="text-rose-900/80 rich-text-content"
                dangerouslySetInnerHTML={{
                  __html: product.about_product || "No description available.",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
